import React, { useState, useMemo } from 'react';
import { FrontendRestaurant } from '../../../services/restaurant/restaurantService';
import { FrontendRestaurantSection } from '../../../services/restaurant/restaurantSectionService';
import { FrontendRestaurantRevenueSource } from '../../../services/restaurant/restaurantRevenueSourceService';
import { RestaurantSectionType } from '../../../types/RestaurantSectionType';

interface ClearDataSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[], restaurantNames: string[]) => void;
  type: 'revenue' | 'orders';
  restaurants: FrontendRestaurant[];
  restaurantSections: Record<string, FrontendRestaurantSection[]>;
  restaurantRevenueSources: Record<string, FrontendRestaurantRevenueSource[]>;
}

const ClearDataSelectionModal: React.FC<ClearDataSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  restaurants,
  restaurantSections,
  restaurantRevenueSources,
}) => {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isRevenue = type === 'revenue';

  // Filter restaurants that have items
  const availableRestaurants = useMemo(() => {
    return restaurants.filter(r => {
      if (isRevenue) {
        return (restaurantRevenueSources[r.id] || []).length > 0;
      } else {
        return (restaurantSections[r.id] || []).length > 0;
      }
    });
  }, [restaurants, restaurantSections, restaurantRevenueSources, isRevenue]);

  // Get items for the selected restaurant
  const items = useMemo(() => {
    if (!selectedRestaurantId) return [];

    if (isRevenue) {
      const sources = restaurantRevenueSources[selectedRestaurantId] || [];
      return sources.map(s => ({
        id: s.id,
        name: s.revenueSourceName,
        label: s.revenueSourceType,
      }));
    } else {
      const sections = restaurantSections[selectedRestaurantId] || [];
      return sections.map(s => ({
        id: s.id,
        name: s.name,
        label: s.restaurantSectionType === RestaurantSectionType.FOOD
          ? 'Food'
          : s.restaurantSectionType === RestaurantSectionType.DRINKS
            ? 'Drinks'
            : 'Non-Food',
      }));
    }
  }, [selectedRestaurantId, restaurantSections, restaurantRevenueSources, isRevenue]);

  const selectedRestaurant = restaurants.find(r => r.id === selectedRestaurantId);

  const handleRestaurantChange = (restaurantId: string) => {
    setSelectedRestaurantId(restaurantId);
    setSelectedIds(new Set());
  };

  const handleClose = () => {
    setSelectedRestaurantId('');
    setSelectedIds(new Set());
    onClose();
  };

  const handleToggleItem = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = items.map(i => i.id);
    const allSelected = allIds.every(id => selectedIds.has(id));

    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const handleConfirm = () => {
    if (!selectedRestaurant) return;
    const selectedArray = Array.from(selectedIds);
    onConfirm(selectedArray, [selectedRestaurant.name]);
    setSelectedRestaurantId('');
    setSelectedIds(new Set());
  };

  const allSelected = items.length > 0 && items.every(i => selectedIds.has(i.id));

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
      onClick={handleClose}
    >
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8 flex-shrink-0">
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {isRevenue ? 'Clear Revenue Data' : 'Clear Order Data'}
            </h2>
            <button
              onClick={handleClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          {/* Restaurant dropdown */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Restaurant
            </label>
            <select
              value={selectedRestaurantId}
              onChange={(e) => handleRestaurantChange(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-zinc-500 dark:focus:ring-zinc-500"
            >
              <option value="">Select a restaurant</option>
              {availableRestaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          {/* Items selection */}
          {selectedRestaurantId && items.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {isRevenue ? 'Revenue Sources' : 'Sections'}
                </label>
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition"
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Scrollable items */}
        {selectedRestaurantId && items.length > 0 && (
          <div className="flex-1 overflow-y-auto px-6 md:px-8 pb-4">
            <div className="space-y-2">
              {items.map(item => (
                <label key={item.id} className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-700 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 transition">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => handleToggleItem(item.id)}
                    className="h-4 w-4 rounded border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-zinc-100 focus:ring-zinc-500 dark:focus:ring-zinc-500"
                  />
                  <span className="text-sm text-zinc-900 dark:text-zinc-100 flex-1">
                    {item.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    item.label === 'Food' || item.label === 'FOOD'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : item.label === 'Drinks' || item.label === 'DRINKS'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400'
                  }`}>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 md:p-8 flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedIds.size === 0}
              className={`flex-1 px-6 py-3 rounded-2xl text-base font-semibold text-white dark:text-white transition disabled:opacity-50 disabled:cursor-not-allowed ${
                isRevenue
                  ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-700'
                  : 'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700'
              }`}
            >
              Delete ({selectedIds.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearDataSelectionModal;
